import { useMemo, useState } from "react";
import { Calculator, Footprints, Info } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { track } from "../lib/api";

// Répartition par segment basée sur des données réelles de course HYROX Open
// (395 452 résultats analysés, temps médians par station — source HyroxDataLab).
// Ordre officiel de la course : 8 courses de 1 km alternées avec 8 stations.
const SEGMENTS = [
  { key: "run1", name: "Course 1 — 1 km", type: "run", w: 5.8 },
  { key: "skierg", name: "SkiErg", type: "station", w: 5.6 },
  { key: "run2", name: "Course 2 — 1 km", type: "run", w: 6.2 },
  { key: "sled_push", name: "Sled Push", type: "station", w: 3.9 },
  { key: "run3", name: "Course 3 — 1 km", type: "run", w: 6.7 },
  { key: "sled_pull", name: "Sled Pull", type: "station", w: 6.3 },
  { key: "run4", name: "Course 4 — 1 km", type: "run", w: 6.7 },
  { key: "burpee", name: "Burpee Broad Jumps", type: "station", w: 6.7 },
  { key: "run5", name: "Course 5 — 1 km", type: "run", w: 6.9 },
  { key: "row", name: "Rowing", type: "station", w: 6.0 },
  { key: "run6", name: "Course 6 — 1 km", type: "run", w: 6.7 },
  { key: "farmers", name: "Farmers Carry", type: "station", w: 2.7 },
  { key: "run7", name: "Course 7 — 1 km", type: "run", w: 6.8 },
  { key: "lunges", name: "Sandbag Lunges", type: "station", w: 6.5 },
  { key: "run8", name: "Course 8 — 1 km", type: "run", w: 7.7 },
  { key: "wallballs", name: "Wall Balls", type: "station", w: 8.6 },
];

function parseTime(str) {
  if (!str) return null;
  const parts = str.trim().split(":").map((p) => p.trim());
  if (parts.some((p) => p === "" || isNaN(Number(p)))) return null;
  let sec = 0;
  if (parts.length === 3) sec = Number(parts[0]) * 3600 + Number(parts[1]) * 60 + Number(parts[2]);
  else if (parts.length === 2) sec = Number(parts[0]) * 60 + Number(parts[1]);
  else return null;
  return sec > 0 ? sec : null;
}

function fmt(sec) {
  sec = Math.round(sec);
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  const mm = String(m).padStart(2, "0");
  const ss = String(s).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${m}:${ss}`;
}

export default function PaceCalculator() {
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState("");
  const total = parseTime(target);

  const plan = useMemo(() => {
    if (!total) return null;
    const sumW = SEGMENTS.reduce((a, b) => a + b.w, 0);
    let cum = 0;
    let runTotal = 0;
    let stationTotal = 0;
    const rows = SEGMENTS.map((seg) => {
      const t = (seg.w / sumW) * total;
      cum += t;
      if (seg.type === "run") runTotal += t; else stationTotal += t;
      return { ...seg, time: t, cum };
    });
    return { rows, runTotal, stationTotal, runAvg: runTotal / 8 };
  }, [total]);

  const openDialog = () => { setOpen(true); track("pace_calc_used", {}); };

  return (
    <>
      <Button data-testid="pace-calc-btn" variant="ghost" size="icon" title="Calculateur d'allure HYROX" onClick={openDialog}>
        <Calculator size={18} />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="pace-calc-dialog" aria-describedby="pace-calc-desc" className="max-w-lg bg-card border-border max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2"><Calculator size={22} className="text-primary" /> Calculateur d'allure HYROX</DialogTitle>
            <DialogDescription id="pace-calc-desc" className="text-muted-foreground">
              Entre ton temps cible total. On répartit des splits par segment.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Temps cible total</Label>
              <Input data-testid="pace-target-input" value={target} onChange={(e) => setTarget(e.target.value)}
                placeholder="ex : 1:15:00 ou 75:00" className="mt-1.5 h-12 text-base bg-secondary border-border" />
              {target && !total && <p className="text-xs text-primary mt-1.5">Format invalide. Utilise hh:mm:ss ou mm:ss.</p>}
            </div>

            {plan && (
              <>
                <div className="grid grid-cols-3 gap-2 text-center" data-testid="pace-summary">
                  <div className="bg-secondary/50 rounded-lg p-3">
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Allure moy. /km</p>
                    <p className="font-display text-xl text-primary">{fmt(plan.runAvg)}</p>
                  </div>
                  <div className="bg-secondary/50 rounded-lg p-3">
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Total course</p>
                    <p className="font-display text-xl">{fmt(plan.runTotal)}</p>
                  </div>
                  <div className="bg-secondary/50 rounded-lg p-3">
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Total stations</p>
                    <p className="font-display text-xl">{fmt(plan.stationTotal)}</p>
                  </div>
                </div>

                <div className="border border-border rounded-lg overflow-hidden" data-testid="pace-splits">
                  {plan.rows.map((r, i) => (
                    <div key={r.key} className={`flex items-center justify-between px-4 py-2.5 text-sm ${i % 2 ? "bg-secondary/20" : ""} ${r.type === "run" ? "" : "font-medium"}`}>
                      <span className="flex items-center gap-2">
                        {r.type === "run" ? <Footprints size={14} className="text-muted-foreground" /> : <span className="w-1.5 h-1.5 rounded-full bg-primary" />}
                        {r.name}
                      </span>
                      <span className="flex items-center gap-3">
                        <span className="tabular-nums">{fmt(r.time)}</span>
                        <span className="tabular-nums text-xs text-muted-foreground w-14 text-right">{fmt(r.cum)}</span>
                      </span>
                    </div>
                  ))}
                </div>

                <div className="flex gap-2 items-start text-xs text-muted-foreground bg-secondary/30 rounded-md p-3">
                  <Info size={14} className="shrink-0 mt-0.5" />
                  <p>Répartition basée sur des <b>données de course réelles HYROX Open</b> (395 452 résultats analysés, temps médians par station — source HyroxDataLab), pas une simple division linéaire. Ta vraie répartition dépend de ton niveau et de tes points forts/faibles (ex : un athlète fort au sled ira plus vite sur ces stations).</p>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
